import { useState } from "react"
import { Link } from "react-router-dom"
import { Loader2, Pencil, Phone, Plus, Quote } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { StatusBadge } from "@/components/ui/status-badge"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { formatTimestamp } from "@/lib/datetime"
import { tcmErrorMessage } from "../../api"
import { useAddBridge, useTcmResources, useUpdateBridge } from "../../hooks"
import { BRIDGE_STATUS, isOpenBridge, NEED_LABELS, OWNER_LABELS } from "../../lib/careplan"
import type { TcmBridge, TcmBridgeOwner, TcmBridgeStatus, TcmBridgeUpdate, TcmNeedKey } from "../../types"

const EDITABLE_STATUS: Exclude<TcmBridgeStatus, "PROPOSED">[] = ["NEEDED", "ARRANGED", "CONFIRMED", "NOT_NEEDED", "DECLINED"]
const CUSTOM = "__custom__"
const NONE = "__none__"

/**
 * What has to be in place at home (home health, equipment, medicines, labs, rides...), who is
 * arranging it and with whom. Pear's pipeline proposes; every field the clinic sets is the
 * clinic's from then on.
 */
export function NeedsPanel({
  episodeId,
  organizationId,
  bridges,
}: {
  episodeId: string
  organizationId: string
  bridges: TcmBridge[]
}) {
  const [editing, setEditing] = useState<TcmBridge | "new" | null>(null)
  const open = bridges.filter(isOpenBridge)
  const rest = bridges.filter((b) => !isOpenBridge(b))
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {open.length ? `${open.length} still to arrange.` : "Nothing left to arrange."} The patient's plan prints each
          one with its contact.
        </p>
        <div className="flex gap-2">
          <Button asChild variant="ghost" size="sm" className="h-8 text-xs">
            <Link to="/tcm/resources">Resource directory</Link>
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" />
            Add a need
          </Button>
        </div>
      </div>
      {bridges.length === 0 ? (
        <div className="rounded-xl border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          The records didn't show anything needed at home. Add one if the patient needs help.
        </div>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {[...open, ...rest].map((b) => (
            <NeedRow key={b.id} b={b} onEdit={() => setEditing(b)} />
          ))}
        </ul>
      )}
      {editing && (
        <NeedDialog
          episodeId={episodeId}
          organizationId={organizationId}
          bridge={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function NeedRow({ b, onEdit }: { b: TcmBridge; onEdit: () => void }) {
  const status = BRIDGE_STATUS[b.status]
  const who = b.effective_resource_name
  const quote = b.citations[0]?.quote
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium">{NEED_LABELS[b.need_key] ?? b.need_key}</span>
          <StatusBadge variant={status.variant} size="sm" dot>{status.label}</StatusBadge>
          {b.owner && <span className="text-[11px] text-muted-foreground">Arranging: {OWNER_LABELS[b.owner]}</span>}
        </div>
        {b.detail && <p className="text-[13px] text-foreground/90">{b.detail}</p>}
        {who ? (
          <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span>{who}</span>
            {b.effective_resource_phone && (
              <a href={`tel:${b.effective_resource_phone}`} className="inline-flex items-center gap-1 hover:underline">
                <Phone className="h-3 w-3" />
                {b.effective_resource_phone}
              </a>
            )}
            {!b.resource_name && !b.clinic_resource_id && <span>(the directory's default)</span>}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">No contact yet: prints as a write-in line.</p>
        )}
        {b.notes && <p className="text-xs text-muted-foreground">Note: {b.notes}</p>}
        {quote && (
          <p className="flex gap-1.5 text-[11px] italic text-muted-foreground">
            <Quote className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              “{quote}”{b.citations[0]?.doc_type ? ` (${b.citations[0].doc_type})` : ""}
            </span>
          </p>
        )}
        {b.updated_by && b.clinic_owned.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Updated by {b.updated_by} · {formatTimestamp(b.updated_at, "M/d h:mm a")}
          </p>
        )}
      </div>
      <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1.5 self-start text-xs" onClick={onEdit}>
        <Pencil className="h-3 w-3" />
        Update
      </Button>
    </li>
  )
}

function NeedDialog({
  episodeId,
  organizationId,
  bridge,
  onClose,
}: {
  episodeId: string
  organizationId: string
  bridge: TcmBridge | null
  onClose: () => void
}) {
  const { toast } = useToast()
  const update = useUpdateBridge(episodeId)
  const add = useAddBridge(episodeId)
  const { data: resources = [] } = useTcmResources(organizationId)
  const [need, setNeed] = useState<TcmNeedKey>(bridge?.need_key ?? "HOME_HEALTH")
  const [detail, setDetail] = useState("")
  const [status, setStatus] = useState<Exclude<TcmBridgeStatus, "PROPOSED">>(
    bridge && bridge.status !== "PROPOSED" ? bridge.status : bridge ? "ARRANGED" : "NEEDED",
  )
  const [owner, setOwner] = useState<TcmBridgeOwner | "">(bridge ? bridge.owner ?? "" : "CLINIC")
  const choices = resources.filter((r) => r.is_active && r.category === need)
  const [resource, setResource] = useState<string>(
    bridge?.clinic_resource_id ?? (bridge?.resource_name ? CUSTOM : NONE),
  )
  const [name, setName] = useState(bridge?.resource_name ?? "")
  const [phone, setPhone] = useState(bridge?.resource_phone ?? "")
  const [notes, setNotes] = useState(bridge?.notes ?? "")
  const busy = update.isPending || add.isPending

  const save = () => {
    const body: TcmBridgeUpdate = {
      status,
      owner: owner || undefined,
      clinic_resource_id: resource !== CUSTOM && resource !== NONE ? resource : null,
      resource_name: resource === CUSTOM ? name.trim() || null : null,
      resource_phone: resource === CUSTOM ? phone.trim() || null : null,
      notes: notes.trim() || null,
    }
    const done = { onSuccess: () => { toast({ title: "Saved" }); onClose() },
                   onError: (err: unknown) => toast({ title: "Not saved", description: tcmErrorMessage(err), variant: "destructive" }) }
    if (bridge) update.mutate({ bridgeId: bridge.id, body }, done)
    else add.mutate({ ...body, need_key: need, detail: detail.trim() || null }, done)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{bridge ? NEED_LABELS[bridge.need_key] : "Add a need"}</DialogTitle>
          <DialogDescription>
            {bridge?.detail ?? "Something the patient needs at home in the next 30 days."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {!bridge && (
            <>
              <div className="grid gap-1.5">
                <Label>Kind of need</Label>
                <Select value={need} onValueChange={(v) => { setNeed(v as TcmNeedKey); setResource(NONE) }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(NEED_LABELS) as TcmNeedKey[]).map((k) => (
                      <SelectItem key={k} value={k}>{NEED_LABELS[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="need-detail">What exactly</Label>
                <Input id="need-detail" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Walker, PT 2x a week…" />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EDITABLE_STATUS.map((s) => (
                    <SelectItem key={s} value={s}>{BRIDGE_STATUS[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Who's arranging it</Label>
              <Select value={owner} onValueChange={(v) => setOwner(v as TcmBridgeOwner)}>
                <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(OWNER_LABELS) as TcmBridgeOwner[]).map((o) => (
                    <SelectItem key={o} value={o}>{OWNER_LABELS[o]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>With whom (prints on the patient's plan)</Label>
            <Select value={resource} onValueChange={setResource}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>The directory's default, or a write-in line</SelectItem>
                {choices.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}{r.phone ? ` · ${r.phone}` : ""}{r.is_default ? " (default)" : ""}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM}>Someone else…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {resource === CUSTOM && (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="need-name">Name</Label>
                <Input id="need-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="need-phone">Phone</Label>
                <Input id="need-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
              </div>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="need-notes">Notes for the team</Label>
            <Textarea id="need-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || (resource === CUSTOM && !name.trim())}>
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
